import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { faChevronDown, faChevronUp, faLock } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { gql, useMutation } from "@outschool/ui-apollo";
import { LearnerFragmentFragment } from "@outschool/ui-components-classroom";
import {
  CollapsedText,
  TextArea,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import { useDimensions, useFormState } from "@outschool/ui-utils";
import ms from "ms";
import React from "react";
import { useDebouncedCallback } from "use-debounce";

import { validOptimisticResponse } from "../../../shared/client/ApolloClient";
import {
  LearnerPrivateNote_LearnerFragment,
  UpdateLearnerNoteMutation,
  UpdateLearnerNoteMutationVariables
} from "../../../shared/generated/graphql";
import * as Learner from "../../../shared/Learner";

export const updateLearnerNoteMutation = gql`
  mutation updateLearnerNote($learnerUid: ID!, $note: String!) {
    updateLearnerNote(learnerUid: $learnerUid, note: $note) {
      uid
      privateNote
    }
  }
`;

const maxNoteLength = 10000;
const maxNoteDisplayLength = 160;
export function EditPrivateNote({ learner }: $TSFixMe) {
  const [updateLearnerNote] = useMutation<
    UpdateLearnerNoteMutation,
    UpdateLearnerNoteMutationVariables
  >(updateLearnerNoteMutation);
  const [debouncedUpdateLearnerNotes, , callPending] = useDebouncedCallback(
    vars => {
      updateLearnerNote(vars);
    },
    ms("1s")
  );

  const { fields } = useFormState({
    privateNote: {
      value: learner?.privateNote ?? "",
      onChange(changedPrivateNote) {
        debouncedUpdateLearnerNotes({
          variables: { learnerUid: learner.uid, note: changedPrivateNote },
          optimisticResponse: validOptimisticResponse({
            updateLearnerNote: {
              uid: learner.uid,
              __typename: "Learner",
              privateNote: changedPrivateNote
            },
            __typename: "updateLearnerNote"
          })
        });
      }
    }
  });

  React.useEffect(
    () => callPending,
    /* Needs to be suppressed because it needs to run once and only once
    if the user edits and hits save faster than the debounce(which is possible)
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  if (!open) {
    return null;
  }
  return (
    <TextArea
      sx={{ minHeight: "72px" }}
      showCount={false}
      maxLength={maxNoteLength}
      {...fields.privateNote}
    />
  );
}
export function LearnerNotesContent({
  learner
}: {
  learner: LearnerFragmentFragment;
  maxIntroLength?: number;
}) {
  const { t } = useTranslation(
    "client\\components\\learner\\LearnerPrivateNote"
  );
  const learnerAge = learner.age;
  const learnerPronoun = learner.pronoun;
  return (
    <>
      <Box>
        <Trans t={t}>
          <Typography
            variant="inherit"
            sx={{
              fontWeight: "fontWeightBold"
            }}
          >
            Age:
          </Typography>
          &nbsp;
          {{ learnerAge }}
        </Trans>
      </Box>
      <Box>
        {/* @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message */}
        {Learner.pronounIsSpecified(learner.pronoun) ? (
          <Trans t={t}>
            <Typography
              variant="inherit"
              sx={{
                fontWeight: "fontWeightBold"
              }}
            >
              Pronouns:
            </Typography>{" "}
            {{ learnerPronoun }}
          </Trans>
        ) : (
          <Trans t={t}>
            <Typography
              variant="inherit"
              sx={{
                fontWeight: "fontWeightBold"
              }}
            >
              Pronouns:
            </Typography>{" "}
            Not specified
          </Trans>
        )}
      </Box>
      <Box
        sx={{
          marginTop: "0.5em"
        }}
      >
        <Typography
          variant="inherit"
          sx={{
            fontWeight: "fontWeightBold"
          }}
        >
          {t`Notes from the Parent:`}
        </Typography>{" "}
        <CollapsedText
          showMoreLabel={t("Show More...")}
          showLessLabel={t("Show Less...")}
          sx={{ color: "gray1", margin: "0px", overflowWrap: "anywhere" }}
          maxCharacters={160}
        >
          {/* @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message */}
          {learner.intro}
        </CollapsedText>
      </Box>
    </>
  );
}

export function LearnerPrivateNote({
  learner
}: {
  learner: LearnerPrivateNote_LearnerFragment;
}) {
  const { t } = useTranslation(
    "client\\components\\learner\\LearnerPrivateNote"
  );
  const [editContainer, setRefEditContainer] = React.useState();
  const { height: editContainerHeight } = useDimensions(editContainer);
  const [isEditing, setIsEditing] = React.useState(false);
  const [trackingNode, setTrackingNode] = React.useState<any>(null);
  const { trackTouch } = useImpressionTracking({
    node: trackingNode,
    trackingLabel: "learner_private_notes",
    uniqueId: learner.uid
  });
  const hasNote = !!learner.privateNote;
  const actionLabel = hasNote ? "Edit" : "Add";
  return (
    <Box
      sx={{
        marginTop: "0.5em"
      }}
    >
      <Box
        flex
        sx={{
          marginTop: "0.5em",
          justifyContent: "space-between"
        }}
      >
        <Typography
          variant="inherit"
          sx={{
            fontWeight: "fontWeightBold"
          }}
        >{t`Private Note:`}</Typography>{" "}
        <Button
          variant="link"
          onClick={() => {
            trackTouch({ action: hasNote ? "edit" : "add" });
            setIsEditing(prev => !prev);
          }}
        >
          <Typography
            variant="inherit"
            sx={{
              marginRight: "2.5px"
            }}
          >
            {isEditing ? "" : actionLabel}
          </Typography>{" "}
          <Icon
            ref={setTrackingNode}
            icon={isEditing ? faChevronUp : faChevronDown}
            sx={{
              fontSize: "0.8em"
            }}
          />
        </Button>
      </Box>
      <Box
        sx={{
          height: hasNote || isEditing ? editContainerHeight : 0,
          overflow: "hidden",
          transition: "height 0.25s",
          marginTop: "4px"
        }}
      >
        <Box ref={setRefEditContainer}>
          {(hasNote || isEditing) && (
            <Box
              sx={{
                marginBottom: "8px"
              }}
            >
              <Icon
                icon={faLock}
                sx={{
                  fontSize: "0.8em",
                  color: "grey.500",
                  marginRight: "8px"
                }}
              />
              <Typography
                variant="inherit"
                sx={{
                  fontSize: "0.875em",
                  fontWeight: "thin",
                  color: "grey.500"
                }}
              >
                {t`This note is not shared with other teachers`}
              </Typography>
            </Box>
          )}{" "}
          <Box>
            {isEditing ? (
              <>
                <EditPrivateNote learner={learner} />
                <Button
                  sx={{
                    marginTop: "12px"
                  }}
                  onClick={() => {
                    trackTouch({ action: "save" });
                    setIsEditing(prev => !prev);
                  }}
                >
                  {t`Save`}
                </Button>
              </>
            ) : (
              <CollapsedText
                showMoreLabel={t("Show More...")}
                showLessLabel={t("Show Less...")}
                onToggle={(_collapsed, fn) => {
                  // NOTE: We are only sending "show_more" for each toggle, and not "show_less"
                  trackTouch({ action: "show_more" });
                  fn();
                }}
                allowCollapse={!isEditing}
                sx={{ color: "gray1", margin: "0px", overflowWrap: "anywhere" }}
                maxCharacters={maxNoteDisplayLength}
              >
                {/* @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message */}
                {learner.privateNote}
              </CollapsedText>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
LearnerPrivateNote.fragments = {
  learner: gql`
    fragment LearnerPrivateNote_learner on Learner {
      uid
      timeZone
      privateNote
    }
  `
};
